// src/features/organisation/components/roles/RoleDetailForm.tsx
import React, { useMemo } from 'react';
import { Shield } from 'lucide-react';
import { Role, RoleCreateDto } from '../../../../types/organisation/roles.types';
import { useRoleCategories, useRoleScopes } from '../../hooks/useReferenceData';
import { rolesApi } from '../../services/organisationApi';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';

interface RoleDetailFormProps {
  roleId: number | null;
  onClose: () => void;
  onSave?: (role: Role) => void;
}

const RoleDetailForm: React.FC<RoleDetailFormProps> = ({
  roleId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { roleCategories } = useRoleCategories();
  const { roleScopes } = useRoleScopes();
  const [loading, setLoading] = React.useState(!!roleId);
  const [role, setRole] = React.useState<Role | null>(null);

  React.useEffect(() => {
    if (roleId) {
      setLoading(true);
      rolesApi.getById(roleId)
        .then((data) => setRole(data))
        .finally(() => setLoading(false));
    } else {
      setRole(null);
    }
  }, [roleId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'roleCode',
      label: 'Code du rôle',
      type: 'text',
      required: true,
      maxLength: 20,
      disabled: !!roleId,
    },
    {
      name: 'roleName',
      label: 'Nom du rôle',
      type: 'text',
      required: true,
      maxLength: 255,
    },
    {
      name: 'categoryCode',
      label: 'Catégorie',
      type: 'select',
      required: true,
      options: roleCategories.map(c => ({ value: c.code, label: c.name })),
      render: (value) => roleCategories.find(c => c.code === value)?.name || value,
    },
    {
      name: 'scopeCode',
      label: 'Portée',
      type: 'select',
      required: true,
      options: roleScopes.map(s => ({ value: s.code, label: s.name })),
      render: (value) => roleScopes.find(s => s.code === value)?.name || value,
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      maxLength: 1000,
      colSpan: 2,
    },
    {
      name: 'isQhseRole',
      label: 'Rôle QHSE',
      type: 'checkbox',
    },
    {
      name: 'isManagerRole',
      label: 'Rôle Management',
      type: 'checkbox',
    },
    {
      name: 'iso9001Flag',
      label: 'ISO 9001',
      type: 'checkbox',
    },
    {
      name: 'iso14001Flag',
      label: 'ISO 14001',
      type: 'checkbox',
    },
    {
      name: 'iso45001Flag',
      label: 'ISO 45001',
      type: 'checkbox',
    },
  ], [roleCategories, roleScopes, roleId]);

  const transformToFormData = (r: Role | null) => {
    if (!r) return {
      roleCode: '', roleName: '', categoryCode: '', scopeCode: '',
      description: '', isQhseRole: false, isManagerRole: false,
      iso9001Flag: false, iso14001Flag: false, iso45001Flag: false,
    };
    return {
      roleCode: r.roleCode,
      roleName: r.roleName,
      categoryCode: r.roleCategory?.code || '',
      scopeCode: r.roleScope?.code || '',
      description: r.description || '',
      isQhseRole: r.isQhseRole,
      isManagerRole: r.isManagerRole,
      iso9001Flag: r.iso9001Flag,
      iso14001Flag: r.iso14001Flag,
      iso45001Flag: r.iso45001Flag,
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    const roleData: RoleCreateDto = {
      roleCode: formData.roleCode,
      roleName: formData.roleName,
      categoryCode: formData.categoryCode,
      scopeCode: formData.scopeCode,
      description: formData.description,
      isQhseRole: formData.isQhseRole,
      isManagerRole: formData.isManagerRole,
      iso9001Flag: formData.iso9001Flag,
      iso14001Flag: formData.iso14001Flag,
      iso45001Flag: formData.iso45001Flag,
    };
    let saved: Role;
    if (roleId) {
      saved = await rolesApi.update(roleId, roleData);
    } else {
      saved = await rolesApi.create(roleData);
    }
    onSaveCallback?.(saved);
    onClose();
    return saved;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: role,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  const infoFields = fields.filter(f => ['roleCode', 'roleName', 'categoryCode', 'scopeCode', 'description'].includes(f.name));
  const flagFields = fields.filter(f => ['isQhseRole', 'isManagerRole', 'iso9001Flag', 'iso14001Flag', 'iso45001Flag'].includes(f.name));

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={role ? role.roleName : 'Nouveau rôle'}
        subtitle={role ? `Code: ${role.roleCode}` : undefined}
        icon={Shield}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <>
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
              <div className="grid grid-cols-2 gap-4">
                {infoFields.map(field => (
                  <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Attributs</h3>
              <div className="grid grid-cols-2 gap-4">
                {flagFields.map(field => (
                  <div key={field.name}>
                    <EditableField
                      field={field}
                      value={formData[field.name]}
                      isEditing={isEditing(field.name)}
                      onStartEdit={() => startEditing(field.name)}
                      onStopEdit={() => stopEditing(field.name)}
                      onChange={(value) => handleFieldChange(field.name, value)}
                      error={errors[field.name]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default RoleDetailForm;
